import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { createOrder, createPayment, createAddress, getAddresses, updateAddress } from '../api';
import toast from 'react-hot-toast';
import { FiCheck, FiMapPin, FiPlus } from 'react-icons/fi';
import './Checkout.css';

const NPR = (n) => 'NPR ' + parseFloat(n || 0).toLocaleString('en-NP');

const NEPAL_PROVINCES = [
  'Koshi Province', 'Madhesh Province', 'Bagmati Province',
  'Gandaki Province', 'Lumbini Province', 'Karnali Province', 'Sudurpashchim Province',
];

const PAYMENT_METHODS = [
  { value: 'cod',           label: 'Cash on Delivery', icon: '💵' },
  { value: 'esewa',         label: 'eSewa',            icon: '🟢' },
  { value: 'khalti',        label: 'Khalti',           icon: '🟣' },
  { value: 'bank_transfer', label: 'Bank Transfer',    icon: '🏦' },
];

const EMPTY_ADDR = {
  line1: '', line2: '', city: 'Kathmandu',
  state: 'Bagmati Province', postal_code: '44600',
  country: 'Nepal', phone: '', notes: '',
  save_address: true, is_default: false,
};

const Checkout = () => {
  const navigate    = useNavigate();
  const { items, cart, clearCart, total } = useCart();
  const { user }    = useAuth();

  const [step, setStep]                   = useState(1);
  const [loading, setLoading]             = useState(false);
  const [order, setOrder]                 = useState(null);

  // saved addresses from DB
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [loadingAddrs, setLoadingAddrs]     = useState(true);
  const [selectedAddrId, setSelectedAddrId] = useState(null); // null = new form
  const [showNewForm, setShowNewForm]       = useState(false);

  // address form state
  const [address, setAddress] = useState(EMPTY_ADDR);

  // payment
  const [paymentMethod, setPaymentMethod] = useState('cod');
  const [paymentRef, setPaymentRef]       = useState('');

  const shipping   = total > 50000 ? 0 : 500;
  const grandTotal = total + shipping;

  // Load saved addresses on mount
  useEffect(() => {
    if (!user) return;
    setLoadingAddrs(true);
    getAddresses()
      .then(({ data }) => {
        const addrs = data.results || [];
        setSavedAddresses(addrs);
        // Auto-select default address
        const def = addrs.find((a) => a.is_default) || addrs[0];
        if (def) {
          setSelectedAddrId(def.id);
          setShowNewForm(false);
          // Auto-populate phone from default address
          if (def.phone) {
            setAddress((prev) => ({ ...prev, phone: prev.phone || def.phone }));
          }
        } else {
          setShowNewForm(true);
        }
      })
      .catch(() => setShowNewForm(true))
      .finally(() => setLoadingAddrs(false));
  }, [user]);

  const handleAddrChange = (e) => {
    const { name, value, type, checked } = e.target;
    setAddress((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  // When user picks a saved address
  const handleSelectSaved = (addr) => {
    setSelectedAddrId(addr.id);
    setShowNewForm(false);
  };

  // Switch to new address form
  const handleNewAddr = () => {
    setSelectedAddrId(null);
    setShowNewForm(true);
    setAddress(EMPTY_ADDR);
  };

  // Validate step 1
  const validateShipping = () => {
    if (!showNewForm && selectedAddrId) return true; // using saved
    if (!address.line1) { toast.error('Address line 1 is required'); return false; }
    if (!address.city)  { toast.error('City is required'); return false; }
    if (!address.phone) { toast.error('Phone number is required'); return false; }
    return true;
  };

  // Build the shipping address string from selected or new
  const buildShippingString = (addr) =>
    `${addr.line1}${addr.line2 ? ', ' + addr.line2 : ''}, ${addr.city}, ${addr.state}, ${addr.postal_code}, ${addr.country}`;

  const handlePlaceOrder = async () => {
    setLoading(true);
    try {
      let finalAddress = null;
      let phoneForOrder = '';
      let notesForOrder = '';
      let shippingString = '';

      if (!showNewForm && selectedAddrId) {
        // Use existing saved address
        finalAddress = savedAddresses.find((a) => a.id === selectedAddrId);
        shippingString = buildShippingString(finalAddress);
        phoneForOrder  = address.phone || finalAddress.phone || '';
        notesForOrder  = address.notes || '';
      } else {
        // Save new address to DB if user wants
        if (address.save_address) {
          const isFirst = savedAddresses.length === 0;
          const { data: newAddr } = await createAddress({
            line1:       address.line1,
            line2:       address.line2,
            city:        address.city,
            state:       address.state,
            postal_code: address.postal_code,
            country:     address.country,
            is_default:  isFirst || address.is_default,
          });
          finalAddress = newAddr;
          setSavedAddresses((prev) => [...prev, newAddr]);
        }
        shippingString = buildShippingString(address);
        phoneForOrder  = address.phone;
        notesForOrder  = address.notes;
      }

      // Place the order
      const { data: orderData } = await createOrder({
        cart:             cart?.id,
        shipping_address: shippingString,
        phone:            phoneForOrder,
        notes:            notesForOrder,
      });
      setOrder(orderData);

      // Record payment
      await createPayment({
        order:     orderData.id,
        amount:    grandTotal,
        provider:  paymentMethod,
        reference: paymentRef || `${paymentMethod.toUpperCase()}-${Date.now()}`,
        status:    'pending',
      });

      localStorage.setItem('dji_last_phone', phoneForOrder);
      await clearCart();
      sessionStorage.setItem('last_order', JSON.stringify(orderData));
      setStep(3);
      toast.success('Order placed successfully!');
    } catch (err) {
      const msg = err.response?.data?.detail || JSON.stringify(err.response?.data) || 'Failed to place order.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  // On mount: restore completed order ONLY if navigating via hard refresh (cart already cleared)
  // Always clear sessionStorage when user has cart items — they are starting a new checkout
  useEffect(() => {
    const saved = sessionStorage.getItem('last_order');
    if (!saved) return;

    // items from CartContext: if cart has anything, this is a new checkout — clear old order
    // Use a tiny delay so CartContext has time to hydrate from localStorage/API
    const timer = setTimeout(() => {
      const guestItems = (() => { try { return JSON.parse(localStorage.getItem('dji_guest_cart')) || []; } catch { return []; } })();
      // Check both guest cart in localStorage and the items already in state
      const cartHasItems = guestItems.length > 0 || items.length > 0;

      if (cartHasItems) {
        // New checkout session — don't restore old confirmation
        sessionStorage.removeItem('last_order');
      } else {
        // Cart is empty, this must be a refresh on the confirmation page
        try {
          setOrder(JSON.parse(saved));
          setStep(3);
        } catch {
          sessionStorage.removeItem('last_order');
        }
      }
    }, 300);

    return () => clearTimeout(timer);

  }, []);

  // Clear sessionStorage when user leaves confirmation
  const handleLeaveConfirmation = (path) => {
    sessionStorage.removeItem('last_order');
    navigate(path);
  };

  if (items.length === 0 && !order && step < 3) { navigate('/cart'); return null; }

  return (
    <div className="checkout-page">
      <div className="container">

        {/* Steps */}
        <div className="checkout-steps">
          {['Shipping', 'Payment', 'Confirmation'].map((s, i) => (
            <div key={s} className={`step ${step > i + 1 ? 'done' : ''} ${step === i + 1 ? 'active' : ''}`}>
              <div className="step-num">{step > i + 1 ? <FiCheck /> : i + 1}</div>
              <span>{s}</span>
              {i < 2 && <div className="step-line" />}
            </div>
          ))}
        </div>

        <div className="checkout-layout">
          <div className="checkout-main">

            {/* ── STEP 1: SHIPPING ── */}
            {step === 1 && (
              <div className="card animate-fade-up">
                <h2 className="section-title" style={{ marginBottom: 24 }}>Shipping Details</h2>

                {loadingAddrs ? (
                  <div className="skeleton" style={{ height: 80, marginBottom: 20 }} />
                ) : savedAddresses.length > 0 && (
                  <div className="saved-addresses">
                    <p className="form-label" style={{ marginBottom: 12 }}>Saved Addresses</p>
                    <div className="saved-addr-list">
                      {savedAddresses.map((a) => (
                        <div
                          key={a.id}
                          className={`saved-addr-card ${selectedAddrId === a.id && !showNewForm ? 'selected' : ''}`}
                          onClick={() => handleSelectSaved(a)}
                        >
                          <div className="addr-radio">
                            <div className={`radio-dot ${selectedAddrId === a.id && !showNewForm ? 'active' : ''}`} />
                          </div>
                          <div className="addr-card-text">
                            <div className="addr-card-line"><FiMapPin /> {a.line1}{a.line2 ? ', ' + a.line2 : ''}</div>
                            <div className="addr-card-sub">{a.city}, {a.state}, {a.postal_code}</div>
                            {a.is_default && <span className="badge badge-green" style={{ marginTop: 6, fontSize: 10 }}>Default</span>}
                          </div>
                        </div>
                      ))}

                      {/* Add new address option */}
                      <div
                        className={`saved-addr-card new-addr-card ${showNewForm ? 'selected' : ''}`}
                        onClick={handleNewAddr}
                      >
                        <div className="addr-radio">
                          <div className={`radio-dot ${showNewForm ? 'active' : ''}`} />
                        </div>
                        <div className="addr-card-text">
                          <div className="addr-card-line"><FiPlus /> Use a new address</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* New address form */}
                {showNewForm && (
                  <div className="new-addr-form">
                    {savedAddresses.length > 0 && <div className="form-divider">New Address</div>}
                    <div className="form-grid">
                      <div className="form-group" style={{ gridColumn: '1/-1' }}>
                        <label className="form-label">Address Line 1 *</label>
                        <input name="line1" value={address.line1} onChange={handleAddrChange} placeholder="House No., Street Name" />
                      </div>
                      <div className="form-group" style={{ gridColumn: '1/-1' }}>
                        <label className="form-label">Address Line 2</label>
                        <input name="line2" value={address.line2} onChange={handleAddrChange} placeholder="Landmark, Area (optional)" />
                      </div>
                      <div className="form-group">
                        <label className="form-label">City *</label>
                        <input name="city" value={address.city} onChange={handleAddrChange} placeholder="Kathmandu" />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Province</label>
                        <select name="state" value={address.state} onChange={handleAddrChange}>
                          {NEPAL_PROVINCES.map((p) => <option key={p}>{p}</option>)}
                        </select>
                      </div>
                      <div className="form-group">
                        <label className="form-label">Postal Code</label>
                        <input name="postal_code" value={address.postal_code} onChange={handleAddrChange} placeholder="44600" />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Country</label>
                        <input name="country" value="Nepal" readOnly />
                      </div>
                      {/* Save address checkboxes */}
                      <div className="form-group" style={{ gridColumn: '1/-1', display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                        <label className="checkbox-label">
                          <input type="checkbox" name="save_address" checked={address.save_address} onChange={handleAddrChange} />
                          Save this address to my account
                        </label>
                        {address.save_address && (
                          <label className="checkbox-label">
                            <input type="checkbox" name="is_default" checked={address.is_default} onChange={handleAddrChange} />
                            Set as default address
                          </label>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Phone & Notes always shown */}
                <div className="form-grid" style={{ marginTop: 20 }}>
                  <div className="form-group" style={{ gridColumn: '1/-1' }}>
                    <label className="form-label">Phone Number *</label>
                    <input name="phone" value={address.phone} onChange={handleAddrChange} placeholder="98XXXXXXXX" />
                  </div>
                  <div className="form-group" style={{ gridColumn: '1/-1' }}>
                    <label className="form-label">Order Notes</label>
                    <textarea name="notes" value={address.notes} onChange={handleAddrChange} placeholder="Any special delivery instructions..." rows={3} />
                  </div>
                </div>

                <button
                  className="btn btn-primary btn-lg"
                  style={{ marginTop: 24 }}
                  onClick={() => { if (validateShipping()) setStep(2); }}
                >
                  Continue to Payment
                </button>
              </div>
            )}

            {/* ── STEP 2: PAYMENT ── */}
            {step === 2 && (
              <div className="card animate-fade-up">
                <h2 className="section-title" style={{ marginBottom: 24 }}>Payment Method</h2>

                {/* Show selected address summary */}
                <div className="addr-summary">
                  <FiMapPin className="addr-summary-icon" />
                  <div>
                    {(() => {
                      if (!showNewForm && selectedAddrId) {
                        const a = savedAddresses.find((x) => x.id === selectedAddrId);
                        if (a) return <><strong>{a.line1}</strong><span>{a.city}, {a.state}</span></>;
                      }
                      return <><strong>{address.line1}</strong><span>{address.city}, {address.state}</span></>;
                    })()}
                    <span>📞 {address.phone}</span>
                  </div>
                  <button className="btn btn-ghost btn-sm" onClick={() => setStep(1)}>Change</button>
                </div>

                <div className="payment-methods" style={{ marginTop: 20 }}>
                  {PAYMENT_METHODS.map((m) => (
                    <label key={m.value} className={`payment-option ${paymentMethod === m.value ? 'selected' : ''}`}>
                      <input type="radio" name="payment" value={m.value} checked={paymentMethod === m.value} onChange={() => setPaymentMethod(m.value)} />
                      <span className="pm-icon">{m.icon}</span>
                      <span className="pm-label">{m.label}</span>
                      {paymentMethod === m.value && <FiCheck className="pm-check" />}
                    </label>
                  ))}
                </div>

                {paymentMethod !== 'cod' && (
                  <div className="form-group" style={{ marginTop: 20 }}>
                    <label className="form-label">Transaction Reference / ID</label>
                    <input value={paymentRef} onChange={(e) => setPaymentRef(e.target.value)} placeholder="Enter transaction ID after payment" />
                  </div>
                )}

                <div className="checkout-nav">
                  <button className="btn btn-outline" onClick={() => setStep(1)}>← Back</button>
                  <button className="btn btn-primary btn-lg" onClick={handlePlaceOrder} disabled={loading}>
                    {loading ? 'Placing Order...' : `Place Order — ${NPR(grandTotal)}`}
                  </button>
                </div>
              </div>
            )}

            {/* ── STEP 3: CONFIRMATION ── */}
            {step === 3 && order && (
              <div className="card order-confirm animate-fade-up">
                <div className="confirm-icon">✅</div>
                <h2>Order Confirmed!</h2>
                <p className="text-muted">Thank you for your order. We'll contact you shortly.</p>
                {order.tracking_id && (
                  <div className="tracking-box">
                    <span className="form-label">Tracking ID</span>
                    <strong>{order.tracking_id}</strong>
                  </div>
                )}
                <div className="confirm-actions">
                  <button className="btn btn-primary" onClick={() => handleLeaveConfirmation('/dashboard')}>View My Orders</button>
                  <button className="btn btn-outline" onClick={() => handleLeaveConfirmation('/products')}>Continue Shopping</button>
                </div>
              </div>
            )}
          </div>

          {/* ── ORDER SUMMARY SIDEBAR ── */}
          {step < 3 && (
            <div className="checkout-sidebar">
              <div className="card">
                <h3 style={{ fontFamily: 'var(--font-condensed)', fontSize: 18, marginBottom: 16 }}>Order Summary</h3>
                <div className="co-items">
                  {items.map((item) => (
                    <div key={item.id} className="co-item">
                      <span className="co-item-name">
                        {(item.second_hand_item || item.product)?.title} × {item.quantity}
                        {item.second_hand_item && <em className="co-item-used"> (pre-owned)</em>}
                      </span>
                      <span>{NPR(parseFloat(item.unit_price) * item.quantity)}</span>
                    </div>
                  ))}
                </div>
                <hr className="divider" />
                <div className="summary-row"><span>Subtotal</span><span>{NPR(total)}</span></div>
                <div className="summary-row">
                  <span>Shipping</span>
                  <span>{shipping === 0 ? <span style={{ color: '#16a34a' }}>Free</span> : NPR(shipping)}</span>
                </div>
                <hr className="divider" />
                <div className="summary-row" style={{ fontWeight: 700, color: 'var(--dji-white)', fontSize: 16 }}>
                  <span>Total</span><span>{NPR(grandTotal)}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Checkout;